import { useCallback, useEffect, useState } from "react";

import { may, send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S13. THE BRIEFING HAD NO EDITOR, AND THAT STOPPED EVERY SHIFT AT THE DOOR.
//
// The state machine reaches checked-in only from the state a worker enters by
// acknowledging a PUBLISHED briefing. The worker app could read
// and acknowledge one since V18; nothing in the console could write or publish one, so
// no shift in the shipped product could be checked in, timed, paid or proven.
//
// THIS SCREEN DECIDES NOTHING, for `briefings.ts`'s reasons: the merge is
// `app.briefing_for`, materiality is `app.publish_briefing`, currency is `0046`'s gate.
// It saves what this event says and asks the database to publish it.
// ---------------------------------------------------------------------------

type Fields = {
  meeting_point: string | null;
  dress_code: string | null;
  duties: string | null;
  contacts: string | null;
  map_url: string | null;
};

type Version = Fields & {
  id: string;
  version_no: number;
  material_version_no: number;
  is_correction: boolean;
  published_at: string;
};

type Briefing = {
  resolved: Fields & { has_template: boolean; has_override: boolean; safety_controls?: string | null };
  versions: Version[];
  unpublishedChanges: boolean;
  /** S110 (0229). Nobody on this post can be briefed or check in until it is published. */
  safetyControlsUnpublished?: boolean;
};

// S145: label and hint are catalogue entries, drawn in the reader's language at render.
const FIELDS: readonly { key: keyof Fields; label: string; hint: string; long?: boolean }[] = [
  { key: "meeting_point", label: "Meeting point", hint: "Where and when to arrive — call time, gate, who to ask for." },
  { key: "dress_code", label: "What to wear", hint: "Uniform, footwear, what is issued on site." },
  { key: "duties", label: "Duties", hint: "What the post involves, in the order the shift runs.", long: true },
  { key: "contacts", label: "Contacts", hint: "Who to call on the day, and how." },
  { key: "map_url", label: "Map link", hint: "A link to the site map or the entrance, if there is one." },
];

function blank(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

export function BriefingEditor({ requirementId, onClose }: { requirementId: string; onClose: () => void }) {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const mayCorrect = me ? may(me, "briefing.correct") : false;
  const [data, setData] = useState<Briefing | null>(null);
  const [draft, setDraft] = useState<Record<keyof Fields, string>>({
    meeting_point: "",
    dress_code: "",
    duties: "",
    contacts: "",
    map_url: "",
  });
  const [correction, setCorrection] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "failed" | "live"; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<{ briefing?: Briefing; error?: { message?: string } }>(
        console_.requirementBriefing(apiBase, requirementId),
      );
      if (res.status !== 200 || !res.body.briefing) {
        setMessage({ tone: "failed", text: refusal(res.body.error) ?? t("This briefing could not be read.") });
        return;
      }
      const b = res.body.briefing;
      setData(b);
      setDraft({
        meeting_point: b.resolved.meeting_point ?? "",
        dress_code: b.resolved.dress_code ?? "",
        duties: b.resolved.duties ?? "",
        contacts: b.resolved.contacts ?? "",
        map_url: b.resolved.map_url ?? "",
      });
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    }
  }, [apiBase, requirementId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(kind: "save" | "publish") {
    setBusy(true);
    setMessage(null);
    try {
      // PUBLISH SAVES FIRST when the text was touched, so "publish" always publishes what
      // is on the screen rather than whatever was last saved.
      const changed =
        data !== null &&
        FIELDS.some(({ key }) => blank(draft[key]) !== (data.resolved[key] ?? null));
      if (kind === "save" || changed) {
        const payload = Object.fromEntries(FIELDS.map(({ key }) => [key, blank(draft[key])]));
        const saved = await send<{ error?: { message?: string } }>(
          console_.saveBriefingOverride(apiBase, requirementId, payload),
        );
        if (saved.status >= 400) {
          setMessage({ tone: "failed", text: refusal(saved.body.error) ?? t("The briefing could not be saved.") });
          return;
        }
      }
      if (kind === "publish") {
        const res = await send<{ briefing?: { versionNo: number }; error?: { message?: string } }>(
          console_.publishBriefing(apiBase, requirementId, correction),
        );
        if (res.status >= 400) {
          setMessage({ tone: "failed", text: refusal(res.body.error) ?? t("The briefing could not be published.") });
          return;
        }
        setMessage({
          tone: "live",
          text: correction
            ? t("Published as version {n} — a correction, so nobody has to read it again.", { n: res.body.briefing?.versionNo ?? "" })
            : t("Published as version {n}. Everyone booked on this post is asked to read it.", { n: res.body.briefing?.versionNo ?? "" }),
        });
      } else {
        setMessage({ tone: "live", text: t("Saved. Nobody sees it until it is published.") });
      }
      setCorrection(false);
      await load();
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <strong>{t("Briefing")}</strong>
        {data === null ? null : data.versions.length === 0 ? (
          <StatusChip tone="neutral" label={t("Never published")} />
        ) : data.unpublishedChanges ? (
          <StatusChip tone="attention" label={t("Version {n} published — changes not yet published", { n: data.versions[0]!.version_no })} />
        ) : (
          <StatusChip tone="live" label={t("Version {n} published", { n: data.versions[0]!.version_no })} />
        )}
      </div>
      {data?.safetyControlsUnpublished ? (
        <div className="row" role="status">
          <StatusChip tone="attention" label={t("Safety controls not published")} />
          <span>
            {t("The signed risk assessment's controls are not in this post's published briefing. Nobody on this post can be briefed or check in until you publish it.")}
          </span>
        </div>
      ) : null}
      <p className="muted">
        {t("Nobody can check in to this post until they have read and acknowledged its latest published briefing.")}
        {data?.resolved.has_template ? ` ${t("Fields left as they are come from the configuration pack.")}` : ""}
      </p>
      {FIELDS.map(({ key, label, hint, long }) => (
        <div className="field" key={key}>
          <label htmlFor={`brief-${requirementId}-${key}`}>{translateLabel(locale, label)}</label>
          {long ? (
            <textarea
              id={`brief-${requirementId}-${key}`}
              rows={5}
              value={draft[key]}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          ) : (
            <input
              id={`brief-${requirementId}-${key}`}
              type={key === "map_url" ? "url" : "text"}
              value={draft[key]}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          )}
          <p className="field-hint">{translateLabel(locale, hint)}</p>
        </div>
      ))}
      {data?.resolved.safety_controls ? (
        <div className="field">
          <strong>{t("Safety controls")}</strong>
          <p className="field-hint">
            {t("From the signed risk assessment for this event and post. Change them on the event's Risk assessment screen — never here, and never as a correction.")}
          </p>
          <p className="pre-line">{data.resolved.safety_controls}</p>
        </div>
      ) : null}
      {mayCorrect && data !== null && data.versions.length > 0 ? (
        <label className="row">
          <input type="checkbox" checked={correction} onChange={(e) => setCorrection(e.target.checked)} />
          <span>{t("A correction only — a typo, nothing anybody needs to read again")}</span>
        </label>
      ) : null}
      {message ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={message.tone} label={message.tone === "failed" ? t("Not applied") : t("Done")} />
          <span>{message.text}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy || data === null} onClick={() => void act("publish")}>
          {busy ? t("Working…") : t("Publish to the crew")}
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy || data === null} onClick={() => void act("save")}>
          {t("Save without publishing")}
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={onClose}>
          {t("Close")}
        </button>
      </div>
      {data !== null && data.versions.length > 0 ? (
        <details>
          <summary>{t("Published versions ({n})", { n: data.versions.length })}</summary>
          <ul className="plain-list">
            {data.versions.map((v) => (
              <li key={v.id}>
                {t("Version {n}", { n: v.version_no })} — {whenIn(v.published_at, orgZone)}
                {v.is_correction ? (
                  <span className="muted"> · {t("correction of version {n}", { n: v.material_version_no })}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
